import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type {
  ServerComparisonResponse,
  ComparisonRow,
  ComparisonWindow,
  GroupRollup,
} from '../../services/serverScorecardService';

/**
 * feat-097 Part D — "Did training move the needle?" benchmark↔post comparison.
 *
 * Renders ABOVE the Recent Shifts table on Server Performance History. For each
 * metric: the server's per-visit % over the fixed Benchmark window vs the Post
 * window, plus the percentage-point delta — grouped Hospitality (¬is_scoring) /
 * Upsell (is_scoring + drink), derived from `is_scoring`, never hardcoded keys.
 *
 * APPROVED HYBRID (design-lead D5, human sign-off 2026-06-24): the compact table
 * [Metric · Benchmark % · Post % · Δ(pp)] mirroring the Recent Shifts chrome,
 * PLUS a dumbbell "Trend" column (hollow benchmark dot → filled coral post dot on
 * a shared 0–100% rail). Always-% (the Part B/C #/% toggle does NOT apply —
 * counts are meaningless across two differently-sized windows). Every value is
 * rendered VERBATIM from the backend /comparison contract (percent, delta_pp,
 * window context) — React never aggregates or subtracts (feedback_metrics_single_source).
 *
 * Delta color rides the SIGN of the change (Option 1, TOKENS.md §1i): ▲ positive /
 * ▼ regress (muted coral, not alarm red) / |Δ|<0.5pp neutral / null Δ → "—".
 *
 * feat-100 Part 2 — each section (Hospitality / Upsell) opens COLLAPSED to a single
 * rollup row (the backend's simple mean of child %s, served additively as
 * `group_rollups`) and toggles INDEPENDENTLY to today's per-metric MetricRows. Both
 * collapsed on first load; no localStorage. The rollup numbers are rendered verbatim
 * from `group_rollups` — React never means/subtracts `comparisons[]` itself.
 *
 * feat-101 (ADR 0019 + Amendment A2) — two independent header toggles, no persistence,
 * default `%`+`Weighted`. The Avg/Weighted toggle is per-metric cross-SHIFT aggregation
 * and drives EVERY row (the A2 fix — it used to touch only the rollup):
 *   1. %/# (`displayMode`) — every value cell (per-metric AND rollup) shows a percentage
 *      in `%` mode, or `count / visits` in `#` mode (rollup = Σcount/Σvisits). The `#`
 *      basis IS the weighted ratio, so in `#` mode the Δ/dumbbell are weighted and the
 *      Avg/Weighted toggle is a no-op (A2-D4); the Δ pill self-labels "pp" (cells lose %).
 *   2. Avg/Weighted (`meanMode`), in `%` mode, on EVERY row:
 *        Weighted → `percent` (per-metric Σcount/Σvisits) / `percent_weighted` (rollup),
 *                   Δ `delta_pp` / `delta_pp_weighted`.
 *        Avg      → `percent_avg` (mean of per-shift rates) on per-metric AND rollup
 *                   (rollup = mean of children's `percent_avg`), Δ `delta_pp_avg`.
 *      Per-metric rows VISIBLY change on toggle (that was the bug A2 fixes).
 * Render matrix → 3 distinct states: `%+Weighted`, `%+Avg`, `#`.
 * No React math (feedback_metrics_single_source / ADR 0019 D6): every number and delta
 * is a backend field; the toggles only SELECT which field a cell renders.
 */

const FLAT_EPS_PP = 0.5; // |Δ| below this reads as flat (neutral pill), per design D7.

// feat-101 — the rollup mean-mode: arithmetic mean of child %s vs visit-weighted.
type MeanMode = 'avg' | 'weighted';

// This card's own %/# mode. feat-234 removed the percentage toggles from the
// Server Scorecard and Recent Shifts and deleted the shared `useMetricDisplayMode`
// hook that used to export this type, so it is re-homed here — the ONE surface
// that still has a %/# choice. This card is out of scope for feat-234 (spec "Out
// of scope"): its percentages are benchmark-vs-post CHANGE over two fixed windows,
// not the station-biased per-metric rate that feature removed.
type MetricDisplayMode = 'count' | 'percent';

// feat-097 Part B/C #/% segmented-control styling. It was originally copied from
// the shared MetricDisplayToggle so the two looked identical; that component was
// deleted in feat-234, so these class strings are now this file's own and no
// other surface depends on them.
const SEG_BASE =
  'px-3 py-1.5 text-sm font-semibold tabular-nums transition-colors focus:outline-none focus:ring-2 focus:ring-coral/40';

interface SegOption<T extends string> {
  value: T;
  label: string;
  hint: string;
}

// A generic segmented control mirroring the feat-097 #/% toggle. Used for both the
// %/# and the Avg/Weighted toggles so they read as siblings. Presentation-only —
// state (and the no-persistence default) lives in BenchmarkComparisonBlock.
function SegmentedToggle<T extends string>({
  ariaLabel,
  value,
  onChange,
  options,
}: {
  ariaLabel: string;
  value: T;
  onChange: (value: T) => void;
  options: SegOption<T>[];
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex rounded-lg border border-softBorder overflow-hidden"
    >
      {options.map((opt, i) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={value === opt.value}
          onClick={() => onChange(opt.value)}
          title={opt.hint}
          className={`${SEG_BASE} ${
            value === opt.value ? 'bg-coral text-white' : 'bg-white text-inkMuted hover:text-ink'
          } ${i > 0 ? 'border-l border-softBorder' : ''}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Format a backend YYYY-MM-DD verbatim (no Date()/timezone shift) → "Jun 15".
function fmtDate(d: string): string {
  const [, m, day] = d.split('-').map((n) => parseInt(n, 10));
  if (!m || !day || m < 1 || m > 12) return d;
  return `${MONTHS[m - 1]} ${day}`;
}
function fmtRange(w: ComparisonWindow): string {
  const [y1, m1] = w.start_date.split('-').map((n) => parseInt(n, 10));
  const [y2, m2, d2] = w.end_date.split('-').map((n) => parseInt(n, 10));
  // Collapse the month when both ends share it: "Jun 15–19" (matches the mockup).
  if (y1 === y2 && m1 === m2 && d2) return `${fmtDate(w.start_date)}–${d2}`;
  return `${fmtDate(w.start_date)}–${fmtDate(w.end_date)}`;
}

// ── A signed-change delta pill. Color is bound to the SIGN, never a level. ──
// feat-101 ② — in # display mode the value cells lose their "%", so the Δ pill
// self-labels its unit (" pp") to keep the count-cells / pp-delta pairing legible;
// % mode stays bare so the default render is byte-identical to feat-100. The unit is
// appended only to a numeric delta (the null "—" carries no unit).
const DeltaPill: React.FC<{ value: number | null; withUnit?: boolean }> = ({ value, withUnit }) => {
  const unit = withUnit ? ' pp' : '';
  if (value === null || value === undefined) {
    // null delta (either side has no data) → neutral "—", never a fabricated 0.
    return (
      <span className="inline-flex items-center rounded-full bg-cream-dark text-delta-flat px-2 py-0.5 text-xs font-bold tabular-nums">
        —
      </span>
    );
  }
  if (Math.abs(value) < FLAT_EPS_PP) {
    return (
      <span className="inline-flex items-center rounded-full bg-cream-dark text-delta-flat px-2 py-0.5 text-xs font-bold tabular-nums">
        — {value.toFixed(1)}{unit}
      </span>
    );
  }
  const up = value > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${
        up ? 'bg-delta-positive/10 text-delta-positive' : 'bg-delta-regress/10 text-delta-regress'
      }`}
    >
      {up ? '▲' : '▼'} {Math.abs(value).toFixed(1)}{unit}
    </span>
  );
};

// A per-visit % cell. null → "—" (a gap, never a fabricated 0). Rendered verbatim.
const Pct: React.FC<{ value: number | null; emphasis?: boolean }> = ({ value, emphasis }) =>
  value === null || value === undefined ? (
    <span className="text-inkMuted/60">—</span>
  ) : (
    <span className={`tabular-nums ${emphasis ? 'font-display text-ink' : 'text-inkMuted'}`}>{value}%</span>
  );

// feat-101 — a Benchmark/Post value cell that renders EITHER the backend `percent`
// (`%` mode) OR `count / visits` (`#` mode). Both come straight from the backend —
// the cell only SELECTS which to show; the "/" is literal text, never a division
// (no React math, ADR 0019 D6). A missing denominator (visits 0/null → the backend
// already nulled `percent`) renders "—" in both modes, mirroring the per-metric
// null convention. Used for per-metric rows AND rollup rows (rollup = Σcount/Σvisits).
const ValueCell: React.FC<{
  percent: number | null;
  count: number | null | undefined;
  visits: number | null | undefined;
  displayMode: MetricDisplayMode;
  emphasis?: boolean;
}> = ({ percent, count, visits, displayMode, emphasis }) => {
  if (displayMode === 'count') {
    if (count === null || count === undefined || visits === null || visits === undefined || visits === 0) {
      return <span className="text-inkMuted/60">—</span>;
    }
    return (
      <span className={`tabular-nums ${emphasis ? 'font-display text-ink' : 'text-inkMuted'}`}>
        {count}
        <span className="text-inkMuted/60"> / {visits}</span>
      </span>
    );
  }
  return <Pct value={percent} emphasis={emphasis} />;
};

// ── Dumbbell: hollow benchmark dot → filled coral post dot on a shared 0–100%
//    rail. Dots are positioned by inline % (runtime values). >100% (possible for
//    a non-greeting per-visit rate) pins the dot at the right edge; the true % is
//    shown in the numeric columns + the dot title (honest number, bounded geometry). ──
const Dumbbell: React.FC<{ bench: number | null; post: number | null }> = ({ bench, post }) => {
  const clamp = (v: number) => Math.max(0, Math.min(v, 100));
  const b = bench === null || bench === undefined ? null : clamp(bench);
  const p = post === null || post === undefined ? null : clamp(post);
  const connector = b !== null && p !== null ? { left: Math.min(b, p), width: Math.abs(b - p) } : null;
  return (
    <div className="relative h-6 w-[180px]" aria-hidden="true">
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-softBorder" />
      {connector && (
        <div
          className="absolute top-1/2 -translate-y-1/2 h-[3px] rounded-full bg-coral/30"
          style={{ left: `${connector.left}%`, width: `${connector.width}%` }}
        />
      )}
      {b !== null && (
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white ring-2 ring-inkMuted"
          style={{ left: `${b}%`, marginLeft: -6 }}
          title={`Benchmark ${bench}%`}
        />
      )}
      {p !== null && (
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-coral ring-2 ring-white shadow"
          style={{ left: `${p}%`, marginLeft: -7 }}
          title={`Post ${post}%`}
        />
      )}
    </div>
  );
};

const RowLabel: React.FC<{ row: ComparisonRow; drink?: boolean }> = ({ row, drink }) => (
  <span className="text-ink">
    {row.is_scoring && (
      <span className="text-gold" aria-hidden="true">
        ★{' '}
      </span>
    )}
    {row.label}
    {drink && <span className="text-inkMuted text-xs"> (drink)</span>}
  </span>
);

// The clickable section-header label cell (chevron + ★ + uppercase title + note).
// Shared by the collapsed rollup row and the expanded subhead so the two states
// read identically — only the chevron direction and the trailing cells differ.
// `note` (collapsed only) appends a rollup descriptor so the rollup value, which
// renders in a per-metric row's exact style, isn't misread as one rate: "· avg of N"
// in avg mean-mode (design-lead tune 1) or "· visit-weighted" in weighted mode
// (feat-101). It stops click propagation so the button and the now-clickable row
// (tune 2) don't double-toggle.
const GroupHeaderButton: React.FC<{
  scoring: boolean;
  label: string;
  expanded: boolean;
  onToggle: () => void;
  note?: string | null;
}> = ({ scoring, label, expanded, onToggle, note }) => {
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      aria-expanded={expanded}
      className="flex items-center gap-2 text-left text-xs font-bold uppercase tracking-wider text-inkMuted hover:opacity-80 transition-opacity"
    >
      <Chevron className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span>
        {scoring && (
          <span className="text-gold" aria-hidden="true">
            ★{' '}
          </span>
        )}
        {label}{' '}
        <span className="font-normal normal-case text-inkMuted/70">
          · {scoring ? 'counts toward the score' : 'reference metrics, do not affect score'}
          {note ? ` · ${note}` : ''}
        </span>
      </span>
    </button>
  );
};

// feat-100 Part 2 — an independently collapsible section (Hospitality / Upsell).
// COLLAPSED (default): one rollup row — the header button in col 1, then the
// section's mean Benchmark % / Post % / Δpp / rollup dumbbell in the same four
// columns a MetricRow uses (no layout shift). EXPANDED: the header button as a
// full-width subhead, followed by today's per-metric MetricRows. The rollup cells
// are rendered VERBATIM from the backend `group_rollups` (feedback_metrics_single_source);
// a missing rollup (pre-feat-100 backend) or a null side → "—", never a recomputed mean.
const GroupSection: React.FC<{
  scoring: boolean;
  label: string;
  rollup: GroupRollup | undefined;
  rows: ComparisonRow[];
  drinkKey?: string | null;
  displayMode: MetricDisplayMode;
  meanMode: MeanMode;
}> = ({ scoring, label, rollup, rows, drinkKey, displayMode, meanMode }) => {
  const [expanded, setExpanded] = React.useState(false); // collapsed by default (AC8)
  const toggle = () => setExpanded((v) => !v);
  // feat-101 A2 — the Avg/Weighted toggle drives the rollup % / Δ / dumbbell / note,
  // but ONLY in % display mode; in # mode the cells are Σcount/Σvisits (the weighted
  // basis), so the rollup is forced weighted and mean-mode is a no-op (A2-D4).
  //   • % + Avg      → percent_avg / delta_pp_avg (mean of the displayed rows; A2-D3).
  //   • % + Weighted → percent_weighted / delta_pp_weighted (pooled Σ/Σ).
  //   • #  (either)  → percent_weighted / delta_pp_weighted for the Δ + dumbbell;
  //                    the value cells render Σcount/Σvisits.
  const avg = displayMode === 'percent' && meanMode === 'avg';
  const benchPct = rollup ? (avg ? rollup.benchmark.percent_avg ?? null : rollup.benchmark.percent_weighted ?? null) : null;
  const postPct = rollup ? (avg ? rollup.post.percent_avg ?? null : rollup.post.percent_weighted ?? null) : null;
  const deltaPp = rollup ? (avg ? rollup.delta_pp_avg ?? null : rollup.delta_pp_weighted ?? null) : null;
  const benchCount = rollup ? rollup.benchmark.count : null;
  const benchVisits = rollup ? rollup.benchmark.visits : null;
  const postCount = rollup ? rollup.post.count : null;
  const postVisits = rollup ? rollup.post.visits : null;
  // Collapsed note: Avg (% mode) → "avg of N" (the rollup = mean of the N displayed
  // rows, A2-D3 + design-lead tune 1); Weighted / # mode → "weighted by visits" (the
  // plainer inline phrasing — the Σ formula lives in the toggle hint; PL-approved copy).
  // Only when a rollup exists (pre-feat-100 backend → no note, as today).
  const note = rollup ? (avg ? `avg of ${rollup.row_count}` : 'weighted by visits') : null;
  return (
    <>
      {/* The whole header row toggles (design-lead tune 2) so the numeric/dumbbell
          cells aren't dead click-zones; the inner button keeps focus + aria-expanded
          for keyboard/SR and stops propagation so a button click toggles only once. */}
      <tr className="bg-cream/70 cursor-pointer hover:bg-cream" onClick={toggle}>
        {expanded ? (
          <td colSpan={5} className="px-6 py-2">
            <GroupHeaderButton scoring={scoring} label={label} expanded onToggle={toggle} />
          </td>
        ) : (
          <>
            <td className="px-6 py-2">
              <GroupHeaderButton
                scoring={scoring}
                label={label}
                expanded={false}
                onToggle={toggle}
                note={note}
              />
            </td>
            <td className="px-4 py-2 text-right w-32">
              <ValueCell percent={benchPct} count={benchCount} visits={benchVisits} displayMode={displayMode} />
            </td>
            <td className="px-4 py-2 text-right w-28">
              <ValueCell percent={postPct} count={postCount} visits={postVisits} displayMode={displayMode} emphasis />
            </td>
            <td className="px-4 py-2 text-right w-28">
              <DeltaPill value={deltaPp} withUnit={displayMode === 'count'} />
            </td>
            <td className="px-6 py-2 w-[230px] align-middle">
              {/* Dumbbell is a rate visual (0–100% rail) — reflects the % shown for the
                  active mode: mean-mode in % mode, always weighted in # mode (①). */}
              <Dumbbell bench={benchPct} post={postPct} />
            </td>
          </>
        )}
      </tr>
      {expanded &&
        rows.map((row) => (
          <MetricRow
            key={row.key}
            row={row}
            drink={drinkKey != null && row.key === drinkKey}
            displayMode={displayMode}
            meanMode={meanMode}
          />
        ))}
    </>
  );
};

// Per-metric row. feat-101 A2: the Avg/Weighted toggle now drives EVERY row (the
// A2 fix). In % mode it selects each side's weighted `percent` (Σcount/Σvisits) vs
// the unweighted `percent_avg` (mean of per-shift rates), and the Δ between
// `delta_pp` and `delta_pp_avg`. In # mode the cells show count/visits (the weighted
// basis) and the Δ is the weighted `delta_pp`, pp-labeled (A2-D4 no-op). The dumbbell
// (a rate visual) reflects whichever % the cells show. All fields backend-served.
const MetricRow: React.FC<{
  row: ComparisonRow;
  drink?: boolean;
  displayMode: MetricDisplayMode;
  meanMode: MeanMode;
}> = ({ row, drink, displayMode, meanMode }) => {
  const avg = displayMode === 'percent' && meanMode === 'avg';
  const benchPct = avg ? row.benchmark.percent_avg ?? null : row.benchmark.percent;
  const postPct = avg ? row.post.percent_avg ?? null : row.post.percent;
  const delta = avg ? row.delta_pp_avg ?? null : row.delta_pp;
  return (
    <tr className="hover:bg-cream-dark/40">
      <td className="px-6 py-3 text-ink max-w-[360px]">
        <RowLabel row={row} drink={drink} />
      </td>
      <td className="px-4 py-3 text-right w-32">
        <ValueCell percent={benchPct} count={row.benchmark.count} visits={row.benchmark.visits} displayMode={displayMode} />
      </td>
      <td className="px-4 py-3 text-right w-28">
        <ValueCell percent={postPct} count={row.post.count} visits={row.post.visits} displayMode={displayMode} emphasis />
      </td>
      <td className="px-4 py-3 text-right w-28">
        <DeltaPill value={delta} withUnit={displayMode === 'count'} />
      </td>
      <td className="px-6 py-3 w-[230px] align-middle">
        <Dumbbell bench={benchPct} post={postPct} />
      </td>
    </tr>
  );
};

const CardShell: React.FC<{ children: React.ReactNode; headerRight?: React.ReactNode }> = ({
  children,
  headerRight,
}) => (
  <div className="bg-white rounded-[20px] shadow-lg overflow-hidden border border-softBorder">
    <div className="bg-gradient-to-r from-coral via-gold to-coral px-6 py-4 flex items-center justify-between flex-wrap gap-2">
      <div>
        <div className="font-display text-base text-white font-bold tracking-wide">
          Training Impact · Benchmark → Post
        </div>
        <div className="text-xs text-white/85">Per-visit attempt rate · Benchmark window → Post window</div>
      </div>
      {headerRight}
    </div>
    {children}
  </div>
);

const WindowChips: React.FC<{ windows: ServerComparisonResponse['windows'] }> = ({ windows }) => (
  <div className="flex items-center gap-2 text-xs flex-wrap">
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-white">
      <span className="inline-block w-2.5 h-2.5 rounded-full ring-2 ring-white bg-transparent" />
      Benchmark · {fmtRange(windows.benchmark)} · {windows.benchmark.shift_count} shifts ·{' '}
      {windows.benchmark.visit_count} visits
    </span>
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-white">
      <span className="inline-block w-2.5 h-2.5 rounded-full bg-white" />
      Post · {fmtRange(windows.post)} · {windows.post.shift_count} shifts · {windows.post.visit_count} visits
    </span>
  </div>
);

interface BenchmarkComparisonBlockProps {
  data: ServerComparisonResponse | null;
  loading?: boolean;
}

const BenchmarkComparisonBlock: React.FC<BenchmarkComparisonBlockProps> = ({ data, loading }) => {
  // feat-101 — two independent, NON-persisted toggles. Default `%`+`Weighted` (A2-D5):
  // per-metric rows stay byte-identical to feat-100 (`percent`); the rollup defaults to
  // `percent_weighted` (forced by #↔% coherence — A2 supersedes feat-100's rollup mean).
  // Declared before any early return so the hooks are always called (rules of hooks).
  const [displayMode, setDisplayMode] = React.useState<MetricDisplayMode>('percent');
  const [meanMode, setMeanMode] = React.useState<MeanMode>('weighted');

  // State 5 — loading skeleton.
  if (loading) {
    return (
      <CardShell>
        <div className="px-6 py-4 space-y-3">
          <div className="h-3 w-24 bg-cream-dark rounded animate-pulse" />
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="h-3 w-44 bg-cream-dark rounded animate-pulse" />
              <div className="h-3 w-16 bg-cream-dark rounded animate-pulse ml-auto" />
              <div className="h-3 w-12 bg-cream-dark rounded animate-pulse" />
              <div className="h-3 w-12 bg-cream-dark rounded animate-pulse" />
              <div className="h-3 w-[180px] bg-cream-dark rounded animate-pulse" />
            </div>
          ))}
        </div>
      </CardShell>
    );
  }

  // No contract (e.g. direct nav without ids, or fetch failed) — render nothing
  // rather than an empty shell.
  if (!data) return null;

  const { windows } = data;
  const benchEmpty = windows.benchmark.shift_count === 0;
  const postEmpty = windows.post.shift_count === 0;

  // State 4 — no shifts in EITHER window: graceful whole-block "no data" body.
  if (benchEmpty && postEmpty) {
    return (
      <CardShell>
        <div className="px-5 py-6 text-center">
          <div className="text-sm text-inkMuted">
            No shift data in the benchmark or post window for this server.
          </div>
          <div className="text-xs text-inkMuted/70 mt-1">
            Comparison will populate as shifts are recorded.
          </div>
        </div>
      </CardShell>
    );
  }

  // State 3 — single-window-only server: one window has no shifts. Keep the
  // present window's context; explain the missing one (never a fabricated number).
  if (benchEmpty || postEmpty) {
    const missingPost = postEmpty;
    const present = missingPost ? windows.benchmark : windows.post;
    const presentLabel = missingPost ? 'Benchmark' : 'Post';
    const missingStart = missingPost ? windows.post.start_date : windows.benchmark.start_date;
    return (
      <CardShell
        headerRight={
          <div className="text-[11px] text-white/90 text-right leading-snug">
            <div>
              {presentLabel} · {present.shift_count} shifts · {present.visit_count} visits
            </div>
            <div className="text-white/70">{missingPost ? 'Post' : 'Benchmark'} · 0 shifts</div>
          </div>
        }
      >
        <div className="px-5 py-4 flex items-start gap-3">
          <svg
            className="w-5 h-5 text-inkMuted/60 shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.8}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m0 3.75h.007M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="text-sm text-ink">
            <div className="font-semibold">
              No shifts in the {missingPost ? 'post-benchmark' : 'benchmark'} window yet.
            </div>
            <div className="text-inkMuted text-xs mt-0.5">
              A comparison appears once this server records a shift in the{' '}
              {missingPost ? 'post-benchmark' : 'benchmark'} window (from {fmtDate(missingStart)}).
              {presentLabel} rates remain in the Recent Shifts table below.
            </div>
          </div>
        </div>
      </CardShell>
    );
  }

  // State 1/2/6 — happy path. Group from is_scoring (+ drink → Upsell), never keys.
  const hospitality = data.comparisons.filter((c) => !c.is_scoring);
  const upsell = [
    ...data.comparisons.filter((c) => c.is_scoring),
    ...(data.drink_comparison ? [data.drink_comparison] : []),
  ];
  // feat-100 Part 2 — match each section's rollup by is_scoring (the same seam the
  // rows are grouped by), never by the `id` string. undefined → collapsed cells "—".
  const rollups = data.group_rollups ?? [];
  const hospitalityRollup = rollups.find((g) => !g.is_scoring);
  const upsellRollup = rollups.find((g) => g.is_scoring);
  const drinkKey = data.drink_comparison?.key ?? null;

  const unitNote = displayMode === 'count' ? '· #' : '· %';

  return (
    <CardShell headerRight={<WindowChips windows={windows} />}>
      {/* feat-101 A2 — two independent toggles. %/# flips every value cell; Avg/Weighted
          flips the rate basis on EVERY row (per-metric + rollup). Non-persisted; default %+Weighted. */}
      <div className="px-6 py-2.5 flex items-center justify-end gap-x-5 gap-y-2 flex-wrap border-b border-softBorder bg-cream/40">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-inkMuted">Values</span>
          <SegmentedToggle<MetricDisplayMode>
            ariaLabel="Value display mode"
            value={displayMode}
            onChange={setDisplayMode}
            options={[
              { value: 'count', label: '#', hint: 'Show underlying numbers (count / visits)' },
              { value: 'percent', label: '%', hint: 'Show percentage of visits' },
            ]}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-inkMuted">Rate</span>
          <SegmentedToggle<MeanMode>
            ariaLabel="Rate aggregation mode"
            value={meanMode}
            onChange={setMeanMode}
            options={[
              { value: 'avg', label: 'Avg', hint: 'Unweighted average of each shift’s rate' },
              { value: 'weighted', label: 'Weighted', hint: 'Visit-weighted: Σ count / Σ visits' },
            ]}
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-inkMuted border-b border-softBorder align-bottom">
              <th className="px-6 py-3 text-left font-bold">Metric</th>
              <th className="px-4 py-3 text-right font-bold w-32">
                Benchmark <span className="normal-case font-normal text-inkMuted/70">{unitNote}</span>
              </th>
              <th className="px-4 py-3 text-right font-bold w-28">
                Post <span className="normal-case font-normal text-inkMuted/70">{unitNote}</span>
              </th>
              <th className="px-4 py-3 text-right font-bold w-28">Δ&nbsp;(pp)</th>
              <th className="px-6 py-3 text-left font-bold w-[230px]">
                Trend <span className="normal-case font-normal text-inkMuted/70">· 0–100%</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-softBorder">
            {hospitality.length > 0 && (
              <GroupSection
                scoring={false}
                label="Hospitality"
                rollup={hospitalityRollup}
                rows={hospitality}
                displayMode={displayMode}
                meanMode={meanMode}
              />
            )}
            {upsell.length > 0 && (
              <GroupSection
                scoring
                label="Upsell"
                rollup={upsellRollup}
                rows={upsell}
                drinkKey={drinkKey}
                displayMode={displayMode}
                meanMode={meanMode}
              />
            )}
          </tbody>
        </table>
      </div>

      {/* Legend — ★/wording parity with the Recent Shifts table + dumbbell key. */}
      <div className="px-6 py-3 border-t border-softBorder flex items-center justify-between flex-wrap gap-2 text-xs text-inkMuted">
        <span>
          <span className="text-gold font-bold" aria-hidden="true">
            ★
          </span>{' '}
          = counts toward the score (upsell-attempt rate). Other rows are reference service metrics and do
          not affect the score.
        </span>
        <span className="flex items-center gap-4">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-white ring-2 ring-inkMuted" /> Benchmark
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-full bg-coral ring-2 ring-white shadow" /> Post
          </span>
        </span>
      </div>
    </CardShell>
  );
};

export default BenchmarkComparisonBlock;
